import { formatNumber, formatString, formatDate } from "@/shared/formats";
import { DEEstadoEnum, DEEstadoInicialEnum, DETipoDocumentoEnum, DocumentoElectronico } from "@/shared/types/documento-electronico";
import { Stack, Typography, Chip, Box, Button, Grid2 as Grid, Paper, CardContent, Divider, Tabs, Tab, Card, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useEnviarDEMutation, useConsultarLoteMutation, useConsultarDEMutation, useDownloadXmlMutation, useDownloadKudeMutation, downloadKude, useReenviarEmailMutation, getKudeTicketDatos } from "../api/de-actions";
import { useActualizarDatosDEMutation } from "../api/actualizar-datos-de";
import {
    Download,
    Send,
    Search,
    ManageSearch,
    PictureAsPdf,
    Print as PrintIcon,
    Receipt,
    Refresh,
    CloudDone as CloudIcon,
    CloudOff as CloudOffIcon,
    Delete as DeleteIcon,
    Email as EmailIcon
} from "@mui/icons-material";
import { notify } from "@/shared/notification";
import { useDeleteDocumentoElectronicoMutation, useDeleteFacturaElectronicaMutation } from "../api/delete-documento-electronico";
import { useEliminarDEReservadoMutation } from "../api/de-actions";
import { buildCamposInutilizacionDE } from "../utils/de-delete-builder";
import { openPdfFromBase64 } from "../utils/pdf-utils";
import { useModalContext } from "@/components/modal";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { Permiso } from "@/shared/types/permiso";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { usePrinter } from "@/components/print";
import KudeTicketView from "./kude-ticket-print/kude-ticket-view";
import useKudePrinter from "../hooks/use-kude-printer";

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

interface DEViewProps {
    de: DocumentoElectronico;
    simpleView?: boolean;
}

const getEstadoChipColor = (estado?: number) => {
    switch (estado) {
        case 0: return "secondary";
        case 1: return "success";
        case 2: return "error";
        case 3: return "default";
        case 4: return "default";
        default: return "default";
    }
};

const getEstadoProcesoChipColor = (estado?: number) => {
    switch (estado) {
        case 0: return "success";
        case 1: return "warning";
        case 2: return "error";
        default: return "info";
    }
};

const getEstadoLabel = (estado?: number) => {
    switch (estado) {
        case 0: return "Inactivo";
        case 1: return "Activo";
        case 2: return "Cancelado";
        case 3: return "Inutilizado";
        case 4: return "Devuelto";
        default: return "Desconocido";
    }
};

const getEstadoProcesoLabel = (estado?: number) => {
    switch (estado) {
        case 0: return "Aprobado";
        case 1: return "Aprobado con Observación";
        case 2: return "Rechazado";
        default: return "En Proceso";
    }
};

const getTipoDocumentoLabel = (tipo?: number) => {
    switch (tipo) {
        case 1: return "Factura Electrónica";
        case 4: return "Autofactura";
        case 5: return "Nota de Crédito";
        case 7: return "Nota de Remisión";
        default: return "Documento Electrónico";
    }
};

const getTipoDocumentoColor = (tipo?: number) => {
    switch (tipo) {
        case 1: return "primary";
        case 4: return "warning";
        case 5: return "error";
        default: return "default";
    }
};

const getTipoDocumentoAsociadoLabel = (tipo?: number) => {
    switch (tipo) {
        case 1: return "Electrónico";
        case 2: return "Impreso";
        case 3: return "Constancia Electrónica";
        default: return "-";
    }
};

const canDeleteDE = (de: DocumentoElectronico) => {
    // Una FE con venta asociada no se puede borrar/cancelar directamente: debe anularse
    // a través del flujo de anulación de venta. Una NCE con venta asociada, en cambio,
    // solo puede existir si esa venta ya fue anulada, por lo que sí se permite borrarla.
    const esFacturaConVenta = de.hasVenta && (
        de.tipoDocumento === DETipoDocumentoEnum.FacturaElectronica
        || de.tipoDocumento === DETipoDocumentoEnum.FacturaElectronicaExportaion
        || de.tipoDocumento === DETipoDocumentoEnum.FacturaElectronicaImportacion
    );

    return !esFacturaConVenta
        && de.estadoInicial != DEEstadoInicialEnum.EnSifen && de.estado != DEEstadoEnum.Cancelado
        && de.estado != DEEstadoEnum.Inutilizado && de.estado != DEEstadoEnum.Devuelto;
}

const canConsultLote = (de: DocumentoElectronico) => {
    return de.estadoInicial == DEEstadoInicialEnum.EnSifen;
}

const canConsultarDE = (de: DocumentoElectronico) => {
    return de.estado == DEEstadoEnum.Inactivo && !!de.cdc;
}

const canSendDE = (de: DocumentoElectronico) => {
    return de.estado == DEEstadoEnum.Inactivo && de.estadoInicial != DEEstadoInicialEnum.EnSifen;
}

const canRegenerateDE = (de: DocumentoElectronico) => {
    return de.estado == DEEstadoEnum.Inactivo && de.estadoInicial != DEEstadoInicialEnum.EnSifen;
}

const keepOutlinedStyleOnLoading = (color: "primary" | "secondary" | "error" | "warning" = "primary") => ({
    '&.Mui-disabled': {
        borderColor: `${color}.main`,
        color: `${color}.main`,
        opacity: 0.6,
    }
});

const keepContainedStyleOnLoading = (color: "primary" | "secondary" = "primary") => ({
    '&.Mui-disabled': {
        backgroundColor: `${color}.main`,
        color: `${color}.contrastText`,
        opacity: 0.6,
    }
});

const canReenviarEmail = (de: DocumentoElectronico) => {
    return de.estado == DEEstadoEnum.Activo;
}

export default function DEView({ de, simpleView = false }: DEViewProps) {
    const { handleClose } = useModalContext("view");
    const enviarDE = useEnviarDEMutation({});
    const consultarLote = useConsultarLoteMutation({});
    const consultarDE = useConsultarDEMutation({});
    const downloadXml = useDownloadXmlMutation({});
    const downloadKudeMutation = useDownloadKudeMutation({});
    const actualizarDatos = useActualizarDatosDEMutation({});
    const borrarDE = useDeleteDocumentoElectronicoMutation({});
    const borrarFE = useDeleteFacturaElectronicaMutation({});
    const eliminarReservado = useEliminarDEReservadoMutation({ onSuccess: handleClose });
    const reenviarEmail = useReenviarEmailMutation({});
    const { hasPermisos } = useAuthContext();
    const canSendEmails = hasPermisos(Permiso.SendEmails);
    const { modeTicket, printPaperWidth } = useConfigurationContext();
    const printer = usePrinter();
    const { printKude, Printer: KudePrinter } = useKudePrinter();
    const [imprimiendoKude, setImprimiendoKude] = useState(false);

    const handleEnviarDE = () => enviarDE.mutate(de.id);
    const handleConsultarLote = () => consultarLote.mutate(de.id);
    const handleConsultarDE = () => consultarDE.mutate(de.id);
    const handleDownloadXml = () => downloadXml.mutate(de.id);
    const handleDescargarKude = () => downloadKudeMutation.mutate(de.id);
    const handleReenviarEmail = () => {
        const emailDestino = de.emailReceptorActual || de.emailReceptor;
        if (!window.confirm(`¿Confirma el reenvío del email a: ${emailDestino}?`)) return;
        reenviarEmail.mutate(de.id);
    };

    const handleBorrarDE = async () => {
        if (window.confirm("¿Está seguro que desea borrar este Documento Electrónico? Esta acción no se puede deshacer.")) {
            if (de.estado === DEEstadoEnum.Reservado) {
                eliminarReservado.mutate(de.id);
            } else if (de.tipoDocumento === DETipoDocumentoEnum.FacturaElectronica) {
                const camposInutilizacion = buildCamposInutilizacionDE(de, "Borrado de Factura Electrónica");
                borrarFE.mutate({ id: de.id, request: camposInutilizacion }, {
                    onSuccess: async (response) => {
                        if (response.nceGenerada) {
                            notify("Nota de Crédito Electrónica generada para anular la Factura Electrónica. Abriendo documento...", { variant: "info" });
                            await printKude({
                                documentTitle: `KUDE N°${response.nceGenerada.docNumDE}`,
                                kudeBase64: response.nceGenerada.kuDEBase64,
                                deId: response.nceGenerada.idDE
                            });
                        }
                    }
                });
            } else {
                const camposInutilizacion = buildCamposInutilizacionDE(de, "Borrado de Documento Electrónico");
                borrarDE.mutate({ id: de.id, request: camposInutilizacion });
            }
        }
    };

    const handleVerKude = async () => {
        try {
            notify("Cargando KuDE...", { variant: "info" });
            const blob = await downloadKude(de.id);
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (error) {
            notify("Error al cargar el KuDE", { variant: "error" });
        }
    };

    const handleImprimirKude = async () => {
        try {
            setImprimiendoKude(true);
            if (modeTicket) {
                const response = await getKudeTicketDatos(de.id);
                await printer.print({
                    documentTitle: `KUDE N°${de.numeroDocumento}`,
                    printComponent: <KudeTicketView datos={response.data} />,
                    pageStyle: `@page { size: ${printPaperWidth} auto; margin: 0; }`
                });
            } else {
                const blob = await downloadKude(de.id);
                const base64 = await blobToBase64(blob);
                openPdfFromBase64(base64);
            }
        } catch (error) {
            notify("Error al imprimir el KuDE", { variant: "error" });
        } finally {
            setImprimiendoKude(false);
        }
    };

    const handleActualizarDatos = () => {
        if (window.confirm("¿Está seguro que desea actualizar los datos del DE? Esto regenerará el documento con los datos actuales del contribuyente y emisor.")) {
            actualizarDatos.mutate(de.id);
        }
    };

    const isAprobado = de.estadoProceso === 0;
    const tipoColor = getTipoDocumentoColor(de.tipoDocumento) as any;
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Box p={2}>
            <Stack gap={2}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        bgcolor: 'action.hover',
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    bgcolor: `${tipoColor}.main`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Receipt sx={{ color: `${tipoColor}.contrastText`, fontSize: 22 }} />
                            </Box>
                            <Stack>
                                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                    {getTipoDocumentoLabel(de.tipoDocumento)}
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    N° {de.numeroDocumento || "N/A"}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            {de.estadoProceso !== undefined && de.estadoProceso !== null && (
                                <Chip
                                    icon={isAprobado ? <CloudIcon /> : <CloudOffIcon />}
                                    label={getEstadoProcesoLabel(de.estadoProceso)}
                                    color={getEstadoProcesoChipColor(de.estadoProceso) as any}
                                />
                            )}
                            <Chip
                                label={getEstadoLabel(de.estado)}
                                color={getEstadoChipColor(de.estado) as any}
                                variant="outlined"
                                size="small"
                            />
                        </Stack>
                    </Stack>
                </Paper>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Información" />
                    <Tab label="Historial" />
                </Tabs>

                {tabValue === 0 && (
                    <Stack gap={2}>
                        <Card variant="outlined">
                            <CardContent sx={{ py: 1 }}>
                                <Typography variant="caption" color="text.secondary">Código de Control (CDC)</Typography>
                                <Typography
                                    variant="body2"
                                    fontFamily="monospace"
                                    sx={{
                                        wordBreak: 'break-all',
                                        bgcolor: 'action.hover',
                                        p: 1,
                                        borderRadius: 1,
                                        mt: 0.5
                                    }}
                                >
                                    {formatString(de.cdc)}
                                </Typography>
                            </CardContent>
                        </Card>

                        <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                            Datos del Documento
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Timbrado</Typography>
                                                <Typography variant="body1" fontWeight="medium">{de.timbrado}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Establecimiento</Typography>
                                                <Typography variant="body1">{de.establecimiento?.padStart(3, '0')}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Punto Expedición</Typography>
                                                <Typography variant="body1">{de.puntoExpedicion?.padStart(3, '0')}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">N° Documento</Typography>
                                                <Typography variant="body1">{de.numeroDocumento}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Fecha Emisión</Typography>
                                                <Typography variant="body1">{formatDate(de.fechaEmisionDE, true)}</Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                            Receptor
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">Nombre / Razón Social</Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {formatString(de.nombreReceptor)}
                                                </Typography>
                                            </Grid>
                                            {de.rucReceptor && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="text.secondary">RUC</Typography>
                                                    <Typography variant="body1">{de.rucReceptor}{de.dvReceptor ? `-${de.dvReceptor}` : ''}</Typography>
                                                </Grid>
                                            )}
                                            {de.docReceptor && !de.rucReceptor && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="text.secondary">Documento</Typography>
                                                    <Typography variant="body1">{de.docReceptor}</Typography>
                                                </Grid>
                                            )}
                                            {de.direccionReceptor && (
                                                <Grid size={12}>
                                                    <Typography variant="caption" color="text.secondary">Dirección</Typography>
                                                    <Typography variant="body2">{de.direccionReceptor}</Typography>
                                                </Grid>
                                            )}
                                            {de.emailReceptor && (
                                                <Grid size={12}>
                                                    <Typography variant="caption" color="text.secondary">Email</Typography>
                                                    <Typography variant="body2">{de.emailReceptor}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                            {de.tipoDocumento == DETipoDocumentoEnum.Autofactura && <Grid size={{ xs: 12, md: 6 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                            Vendedor
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">Nombre</Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {formatString(de.nombreVendedor)}
                                                </Typography>
                                            </Grid>
                                            {de.docVendedor && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="text.secondary">Documento</Typography>
                                                    <Typography variant="body1">{de.docVendedor}</Typography>
                                                </Grid>
                                            )}
                                            {de.direccionVendedor && (
                                                <Grid size={12}>
                                                    <Typography variant="caption" color="text.secondary">Dirección</Typography>
                                                    <Typography variant="body2">{de.direccionVendedor}</Typography>
                                                </Grid>
                                            )}
                                            {de.emailVendedor && (
                                                <Grid size={12}>
                                                    <Typography variant="caption" color="text.secondary">Email</Typography>
                                                    <Typography variant="body2">{de.emailVendedor}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>}
                            {de.cdcDocumentoAsociado && <Grid size={{ xs: 12, md: 6 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                            Documento Asociado
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">Tipo</Typography>
                                                <Typography variant="body1">{getTipoDocumentoAsociadoLabel(de.tipoDocumentoAsociado)}</Typography>
                                            </Grid>
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">CDC</Typography>
                                                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                                                    {de.cdcDocumentoAsociado}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>}
                        </Grid>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                    Totales
                                </Typography>
                                <Grid container spacing={1.5}>
                                    {de.cMoneOpe !== "PYG" && <Grid size={{ xs: 6, sm: de.cMoneOpe !== "PYG" ? 2 : 3 }}>
                                        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Tipo de Cambio</Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {formatNumber(de.dTiCam, 0)}
                                            </Typography>
                                        </Paper>
                                    </Grid>}
                                    <Grid size={{ xs: 6, sm: de.cMoneOpe !== "PYG" ? 2.25 : 3 }}>
                                        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">IVA 5%</Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {de.cMoneOpe} {formatNumber(de.subIva5, 0)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: de.cMoneOpe !== "PYG" ? 2.25 : 3 }}>
                                        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">IVA 10%</Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {de.cMoneOpe} {formatNumber(de.subIva10, 0)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: de.cMoneOpe !== "PYG" ? 2.25 : 3 }}>
                                        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Exentas</Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {de.cMoneOpe} {formatNumber(de.subIvaExe, 0)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.selected', borderRadius: 1, textAlign: 'center', border: 1, borderColor: 'primary.main' }}>
                                            <Typography variant="caption" color="primary">Total</Typography>
                                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                                                {de.cMoneOpe} {formatNumber(de.total, 0)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {(de.codigoProceso || de.descripcionProceso || de.numeroLote) && (
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={1}>
                                        Estado SIFEN
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {de.codigoProceso && (
                                            <Grid size={{ xs: 6, sm: 4 }}>
                                                <Typography variant="caption" color="text.secondary">Código</Typography>
                                                <Typography variant="body1">{formatString(de.codigoProceso)}</Typography>
                                            </Grid>
                                        )}
                                        {de.numeroLote && (
                                            <Grid size={{ xs: 6, sm: 4 }}>
                                                <Typography variant="caption" color="text.secondary">Número de Lote</Typography>
                                                <Typography variant="body1">{de.numeroLote}</Typography>
                                            </Grid>
                                        )}
                                        {de.descripcionProceso && (
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">Descripción</Typography>
                                                <Typography variant="body1">{formatString(de.descripcionProceso)}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Stack>
                )}

                {tabValue === 1 && (
                    <Card variant="outlined" >
                        <CardContent sx={{ padding: 2, paddingBottom: 1 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="medium" mb={2}>
                                Historial de Acciones
                            </Typography>
                            {de.respuestasSifen && de.respuestasSifen.length > 0 ? (
                                <Stack gap={2}>
                                    {de.respuestasSifen.map((respuesta, index) => (
                                        <Card key={index} variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                                            <CardContent sx={{ padding: 2 }}>
                                                <Stack gap={2}>
                                                    <Stack direction="row" flexWrap="wrap" gap={5} rowGap={1.5}>
                                                        <Stack>
                                                            <Typography variant="caption" color="text.secondary">Fecha</Typography>
                                                            <Typography variant="body2">{formatDate(respuesta.fecha)}</Typography>
                                                        </Stack>
                                                        {respuesta.codigo && (
                                                            <Stack>
                                                                <Typography variant="caption" color="text.secondary">Código</Typography>
                                                                <Typography variant="body2" fontWeight="medium">{formatString(respuesta.codigo)}</Typography>
                                                            </Stack>
                                                        )}
                                                        {respuesta.titulo && (
                                                            <Stack>
                                                                <Typography variant="caption" color="text.secondary">Título</Typography>
                                                                <Typography variant="body2">{formatString(respuesta.titulo)}</Typography>
                                                            </Stack>
                                                        )}
                                                        {respuesta.numeroLote && (
                                                            <Stack>
                                                                <Typography variant="caption" color="text.secondary">Número de Lote</Typography>
                                                                <Typography variant="body2">{formatString(respuesta.numeroLote)}</Typography>
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                    {respuesta.descripcion && (
                                                        <Stack>
                                                            <Typography variant="caption" color="text.secondary">Descripción</Typography>
                                                            <Typography variant="body2">{respuesta.descripcion}</Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                                    No hay historial de acciones disponible
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                )}

                <Divider />
                <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="flex-end">
                    {de.estado !== DEEstadoEnum.Reservado && de.cdc && <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdf />}
                        onClick={handleVerKude}
                    >
                        Ver KuDE
                    </Button>}
                    {de.estado !== DEEstadoEnum.Reservado && de.cdc && <Button
                        variant="outlined"
                        size="small"
                        startIcon={imprimiendoKude ? <CircularProgress size={18} /> : <PrintIcon />}
                        onClick={handleImprimirKude}
                        disabled={imprimiendoKude}
                        sx={keepOutlinedStyleOnLoading()}
                    >
                        Imprimir KuDE
                    </Button>}
                    {!simpleView && de.estado !== DEEstadoEnum.Reservado && (
                        <>
                            {de.cdc && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={downloadKudeMutation.isPending ? <CircularProgress size={18} /> : <Download />}
                                    onClick={handleDescargarKude}
                                    disabled={downloadKudeMutation.isPending}
                                    sx={keepOutlinedStyleOnLoading()}
                                >
                                    Descargar KuDE
                                </Button>
                            )}

                            {de.cdc && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={downloadXml.isPending ? <CircularProgress size={18} /> : <Download />}
                                    onClick={handleDownloadXml}
                                    disabled={downloadXml.isPending}
                                    sx={keepOutlinedStyleOnLoading()}
                                >
                                    Descargar XML
                                </Button>
                            )}

                            {canSendEmails && canReenviarEmail(de) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={reenviarEmail.isPending ? <CircularProgress size={18} /> : <EmailIcon />}
                                    onClick={handleReenviarEmail}
                                    disabled={reenviarEmail.isPending}
                                    sx={keepOutlinedStyleOnLoading()}
                                >
                                    Reenviar Email
                                </Button>
                            )}

                            {canRegenerateDE(de) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="warning"
                                    startIcon={actualizarDatos.isPending ? <CircularProgress size={18} /> : <Refresh />}
                                    onClick={handleActualizarDatos}
                                    disabled={actualizarDatos.isPending}
                                    sx={keepOutlinedStyleOnLoading("warning")}
                                >
                                    Regenerar
                                </Button>
                            )}

                            {canSendDE(de) && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={enviarDE.isPending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                                    onClick={handleEnviarDE}
                                    disabled={enviarDE.isPending}
                                    sx={keepContainedStyleOnLoading()}
                                >
                                    Enviar DE
                                </Button>
                            )}

                            {de.numeroLote && canConsultLote(de) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    startIcon={consultarLote.isPending ? <CircularProgress size={18} color="inherit" /> : <Search />}
                                    onClick={handleConsultarLote}
                                    disabled={consultarLote.isPending}
                                    sx={keepOutlinedStyleOnLoading("secondary")}
                                >
                                    Consultar Lote
                                </Button>
                            )}

                            {de.cdc && canConsultarDE(de) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    startIcon={consultarDE.isPending ? <CircularProgress size={18} color="inherit" /> : <ManageSearch />}
                                    onClick={handleConsultarDE}
                                    disabled={consultarDE.isPending}
                                    sx={keepOutlinedStyleOnLoading("secondary")}
                                >
                                    Consultar DE
                                </Button>
                            )}

                            {canDeleteDE(de) && <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={(borrarDE.isPending || borrarFE.isPending) ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                                onClick={handleBorrarDE}
                                disabled={borrarDE.isPending || borrarFE.isPending}
                                sx={keepOutlinedStyleOnLoading("error")}
                            >
                                Borrar DE
                            </Button>}
                        </>
                    )}
                </Stack>
            </Stack>
            <printer.Printer />
            <KudePrinter />
        </Box >
    );
}
